import { useState, useEffect } from 'react';
import { Accessibility, RotateCcw, Plus, Minus, Sun, Link2, Type, Eye, EyeOff, Contrast, X } from 'lucide-react';

const FILTER_OPTIONS = [
  { key: 'grayscale', label: 'Grayscale', icon: Eye },
  { key: 'highContrast', label: 'High Contrast', icon: Contrast },
  { key: 'negative', label: 'Negative Contrast', icon: EyeOff },
  { key: 'lightBg', label: 'Light Background', icon: Sun },
  { key: 'underline', label: 'Links Underline', icon: Link2 },
  { key: 'readable', label: 'Readable Font', icon: Type },
];

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState({});
  const [fontSize, setFontSize] = useState(100);

  useEffect(() => {
    const body = document.body;
    body.classList.toggle('a11y-grayscale', !!active.grayscale);
    body.classList.toggle('a11y-high-contrast', !!active.highContrast);
    body.classList.toggle('a11y-negative', !!active.negative);
    body.classList.toggle('a11y-light-bg', !!active.lightBg);
    body.classList.toggle('a11y-links-underline', !!active.underline);
    body.classList.toggle('a11y-readable-font', !!active.readable);
  }, [active]);

  useEffect(() => {
    // Apply font size to ALL text elements inside main-wrapper
    const wrapper = document.getElementById('main-wrapper');
    if (!wrapper) return;
    const scale = fontSize / 100;
    wrapper.style.fontSize = `${fontSize}%`;
    // Force all child text elements to inherit
    const allElements = wrapper.querySelectorAll('p, span, a, li, td, th, h1, h2, h3, h4, h5, h6, label, input, button, textarea, select, div');
    allElements.forEach(el => {
      if (fontSize !== 100) {
        const computed = window.getComputedStyle(el);
        const currentSize = parseFloat(computed.fontSize);
        // Only set if element doesn't already have our custom property
        if (!el.dataset.origFontSize) {
          el.dataset.origFontSize = currentSize;
        }
      } else {
        // Reset
        delete el.dataset.origFontSize;
      }
    });
  }, [fontSize]);

  const increaseText = () => setFontSize(f => Math.min(f + 10, 150));
  const decreaseText = () => setFontSize(f => Math.max(f - 10, 70));
  const toggleFilter = (key) => setActive(prev => ({ ...prev, [key]: !prev[key] }));
  const reset = () => { setActive({}); setFontSize(100); };
  const isAnyActive = Object.values(active).some(Boolean) || fontSize !== 100;

  return (
    <div className="fixed left-0 top-20 z-[60]" data-testid="accessibility-widget">
      <button onClick={() => setOpen(!open)} className={`w-10 h-10 flex items-center justify-center shadow-lg transition-colors ${isAnyActive ? 'bg-[#C5A028] text-white' : 'bg-white text-[#1C1917] border border-[#E8E2D9]'}`} aria-label="Accessibility options" data-testid="a11y-toggle">
        <Accessibility className="w-5 h-5" />
      </button>
      {open && (
        <div className="absolute left-0 top-12 bg-white border border-[#E8E2D9] shadow-xl w-60 p-3" data-testid="a11y-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A028]">Accessibility</p>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#FAF8F4] rounded transition-colors" aria-label="Close accessibility panel" data-testid="a11y-close">
              <X className="w-4 h-4 text-[#78716C]" />
            </button>
          </div>

          {/* Text Size Controls */}
          <div className="mb-2 p-2 bg-[#FAF8F4] rounded-sm">
            <p className="text-[10px] font-semibold text-[#78716C] mb-2 uppercase tracking-wider">Text Size: {fontSize}%</p>
            <div className="flex gap-2">
              <button onClick={decreaseText} disabled={fontSize <= 70} className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium border border-[#E8E2D9] bg-white hover:border-[#C5A028] transition-colors disabled:opacity-40" data-testid="a11y-text-decrease">
                <Minus className="w-3 h-3" /> Smaller
              </button>
              <button onClick={increaseText} disabled={fontSize >= 150} className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium border border-[#E8E2D9] bg-white hover:border-[#C5A028] transition-colors disabled:opacity-40" data-testid="a11y-text-increase">
                <Plus className="w-3 h-3" /> Larger
              </button>
            </div>
          </div>

          {/* Filter Toggles */}
          <div className="space-y-1">
            {FILTER_OPTIONS.map(o => (
              <button key={o.key} onClick={() => toggleFilter(o.key)} className={`w-full flex items-center gap-3 px-3 py-2 text-xs font-medium transition-colors rounded-sm ${active[o.key] ? 'bg-[#C5A028] text-white' : 'hover:bg-[#FAF8F4] text-[#1C1917]'}`} data-testid={`a11y-${o.key}`}>
                <o.icon className="w-4 h-4 flex-shrink-0" />
                {o.label}
              </button>
            ))}
          </div>

          {isAnyActive && (
            <button onClick={reset} className="w-full flex items-center justify-center gap-2 mt-3 py-2 text-xs font-medium text-[#78716C] border border-[#E8E2D9] hover:border-[#C5A028] transition-colors" data-testid="a11y-reset">
              <RotateCcw className="w-3 h-3" /> Reset All
            </button>
          )}
        </div>
      )}
    </div>
  );
}
